import { jimHeadAssets, jimVisualAssets } from "./assets";
import type { JimMood, JimVisual } from "./types";
import { randomBetween } from "./utils";

type IdleVisualWeight = {
  visual: JimVisual;
  weight: number;
  minMs: number;
  maxMs: number;
};

export const loginFailureMoods: JimMood[] = ["setup", "thinking", "warning", "listening", "locked"];
export const loginSuccessThinkingMs = 1160;
export const loginSuccessIdeaMs = 1040;

const idleVisualWeights: IdleVisualWeight[] = [
  { visual: "idle", weight: 34, minMs: 3200, maxMs: 5600 },
  { visual: "listening", weight: 14, minMs: 2200, maxMs: 3800 },
  { visual: "setup", weight: 8, minMs: 2200, maxMs: 3600 },
  { visual: "success", weight: 7, minMs: 2200, maxMs: 3600 },
  { visual: "thinking", weight: 6, minMs: 2200, maxMs: 3800 },
  { visual: "laughing", weight: 4, minMs: 1800, maxMs: 3000 },
  { visual: "warning", weight: 3, minMs: 1800, maxMs: 2800 },
  { visual: "locked", weight: 1, minMs: 1600, maxMs: 2600 },
  { visual: "thinkingGif", weight: 3, minMs: 3000, maxMs: 4600 }
];

let jimIdleTimer: number | undefined;
let jimIdleActive = false;
let lastJimVisual: JimVisual = "idle";
let forceVisibleIdleStep = false;

export function jimHeadMarkup(mood: JimMood, className = ""): string {
  return `<img class="jim-head-state ${className}" data-jim-head data-mood="${mood}" src="${jimHeadAssets[mood]}" alt="" aria-hidden="true" />`;
}

export function staticJimHeadMarkup(mood: JimMood, className = ""): string {
  return `<img class="jim-head-state ${className}" src="${jimHeadAssets[mood]}" alt="" aria-hidden="true" />`;
}

export function setJimVisual(visual: JimVisual, options: { restart?: boolean } = {}): void {
  lastJimVisual = visual;
  document.querySelectorAll<HTMLImageElement>("[data-jim-head]").forEach((image) => {
    const src = jimVisualAssets[visual];
    image.src = options.restart ? `${src}?play=${Date.now()}` : src;
    image.dataset.mood = visual;
  });
}

export function setJimMood(mood: JimMood): void {
  setJimVisual(mood);
}

export function clearJimIdleTimer(): void {
  if (jimIdleTimer !== undefined) {
    window.clearTimeout(jimIdleTimer);
    jimIdleTimer = undefined;
  }
}

function focusedTextInput(): boolean {
  const activeElement = document.activeElement;
  return activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement;
}

function loginTextInputFocused(): boolean {
  const activeElement = document.activeElement;
  return Boolean(activeElement?.closest?.(".login-screen")) && focusedTextInput();
}

function chooseIdleVisual(): { visual: JimVisual; minMs: number; maxMs: number } {
  const candidates = idleVisualWeights.filter((item) => item.visual !== lastJimVisual && (!forceVisibleIdleStep || item.visual !== "idle"));
  const totalWeight = candidates.reduce((total, item) => total + item.weight, 0);
  let roll = Math.random() * totalWeight;
  for (const item of candidates) {
    roll -= item.weight;
    if (roll <= 0) return item;
  }
  return candidates[0] || idleVisualWeights[0];
}

function scheduleJimIdleLoop(delayMs = randomBetween(6500, 14500)): void {
  clearJimIdleTimer();
  if (!jimIdleActive) return;

  jimIdleTimer = window.setTimeout(() => {
    if (!jimIdleActive || (focusedTextInput() && !loginTextInputFocused()) || !document.querySelector("[data-jim-head]")) {
      scheduleJimIdleLoop(randomBetween(5000, 10000));
      return;
    }

    const next = chooseIdleVisual();
    forceVisibleIdleStep = false;
    setJimVisual(next.visual);

    if (next.visual === "thinkingGif") {
      jimIdleTimer = window.setTimeout(() => {
        if (!jimIdleActive || (focusedTextInput() && !loginTextInputFocused()) || !document.querySelector("[data-jim-head]")) return;

        if (Math.random() < 0.28) {
          setJimVisual("ideaGif");
          scheduleJimIdleLoop(randomBetween(5200, 12000));
          return;
        }

        setJimMood("idle");
        scheduleJimIdleLoop(randomBetween(6200, 14000));
      }, randomBetween(next.minMs, next.maxMs));
      return;
    }

    scheduleJimIdleLoop(randomBetween(next.minMs + 5200, next.maxMs + 10500));
  }, delayMs);
}

export function startJimIdleLoop(delayMs?: number): void {
  jimIdleActive = true;
  forceVisibleIdleStep = true;
  scheduleJimIdleLoop(delayMs);
}

export function stopJimIdleLoop(): void {
  jimIdleActive = false;
  clearJimIdleTimer();
}

export function loginFailureMood(failureCount: number): JimMood {
  const index = Math.min(Math.max(failureCount, 1), loginFailureMoods.length) - 1;
  return loginFailureMoods[index];
}

export function messageIsLockedOut(message: string): boolean {
  return /0\s+attempts?\s+left|locked|disabled|speak to admin/i.test(message);
}

export function bindJimListening(
  root: ParentNode,
  defaultMood: () => JimMood,
  canRunLoginIdle = () => false
): void {
  root.querySelectorAll<HTMLInputElement>("input, textarea").forEach((input) => {
    input.addEventListener("focus", () => {
      stopJimIdleLoop();
      setJimMood("thinking");
      if (input.closest(".login-screen") && canRunLoginIdle()) {
        startJimIdleLoop(randomBetween(3600, 7200));
      }
    });
    input.addEventListener("blur", () => {
      setJimMood(defaultMood());
      startJimIdleLoop(randomBetween(6000, 13000));
    });
  });
}
